import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree, CanActivateFn } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TokenService } from './service/token.service';
import { UserManagementService } from './service/user-management.service';
import { MatDialog } from '@angular/material/dialog';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { ProgramSelectionService } from './service/program-selection.service';

/** Module-level flag: prevents duplicate "Access Denied" dialogs from spawning. */
let isUnauthorizedDialogOpen = false;

export const authGuardFn: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const tokenService = inject(TokenService);
  const userManagementService = inject(UserManagementService);
  const dialog = inject(MatDialog);
  const programSelectionService = inject(ProgramSelectionService);

  // Check if the URL contains an authorization code
  const urlParams = new URLSearchParams(window.location.search);
  const authCode = urlParams.get('code');
  if (authCode) {
    console.log('[AuthGuard] Auth code detected in URL. Redirecting to /login to handle exchange.', {
      hasState: !!urlParams.get('state')
    });
    // Redirect to login with the full query params so LoginComponent can exchange the code
    const queryParams: { [key: string]: string } = {};
    urlParams.forEach((value, key) => {
      queryParams[key] = value;
    });
    return of(router.createUrlTree(['/login'], { queryParams }));
  }

  const idToken = tokenService.getToken();

  if (!idToken) {
    console.warn('[AuthGuard] No token found. Redirecting to /login.');
    return of(router.createUrlTree(['/login'])); // No token, redirect to login
  }

  // If token looks like a JWT (contains two dots), validate expiry.
  const looksLikeJwt = typeof idToken === 'string' && idToken.split('.').length === 3;
  if (looksLikeJwt) {
    try {
      const tokenPayload = tokenService.parseToken(idToken);
      if (tokenService.isTokenExpired(tokenPayload)) {
        console.warn('[AuthGuard] Token expired. Redirecting to /login.');
        return of(router.createUrlTree(['/login'])); // Token expired
      }
    } catch (error) {
      console.error('Invalid JWT token:', error);
      console.warn('[AuthGuard] Token parse failed. Redirecting to /login.');
      return of(router.createUrlTree(['/login'])); // Redirect on token parsing failure
    }
  }

  // Blocking guard: check user permissions and groups before allowing route transition.
  // If a 403 comes back, show the "Access Denied" dialog and block navigation (return false).
  console.log('[AuthGuard] Checking user permissions before permitting navigation...');
  return userManagementService.getUserGroups().pipe(
    map(() => {
      console.log('[AuthGuard] Permissions verified. Navigation permitted.');
      return true;
    }),
    catchError(err => {
      console.error('Error fetching user groups in AuthGuard:', err);
      if (err?.status === 403 && !isUnauthorizedDialogOpen) {
        isUnauthorizedDialogOpen = true;
        // 1. Prevent program selection popup from showing (clears localStorage flag)
        programSelectionService.suppressPopup();
        // 2. Close any dialogs already open
        dialog.closeAll();
        // 3. err.error is the parsed response body: { message: "...", status: false, errors: [] }
        const message = err?.error?.message || 'You are not authorized to access this portal.';
        const dialogRef = dialog.open(UnauthorizedComponent, {
          disableClose: true,
          data: { message }
        });
        dialogRef.afterClosed().subscribe(() => {
          isUnauthorizedDialogOpen = false;
        });
      }
      // Block navigation so the target route/component never mounts or renders
      return of(false);
    })
  );
};
